<template>
  <custom-dialog
    color="primary"
    ref="dialog"
    icon="business"
    store-modal
    :title="
      generateMessage(
        `heatingSetupParams.${isEditing ? 'update' : 'create'}.title`
      )
    "
  >
    <v-simple-table
      id="non-editable-table"
      class="secondarybackground"
      :class="{
        'non-editable-table-dark': darkThemeEnabled,
        'non-editable-table-light': !darkThemeEnabled,
      }"
    >
      <tbody>
        <tr>
          <td class="font-weight-bold">
            Numéro d'installation
          </td>
          <td>
            {{ item.code }}
          </td>
          <td class="font-weight-bold">
            Adresse
          </td>
          <td>{{ item.street }} - {{ item.city }}</td>
        </tr>
        <tr>
          <td class="font-weight-bold">
            Nom
          </td>
          <td>
            {{ item.name }}
          </td>
          <td class="font-weight-bold">
            Equipe
          </td>
          <td>
            {{ item.entity_id }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">
            Type de marché
          </td>
          <td>
            {{ item.market_type }}
          </td>
          <td class="font-weight-bold">
            Usage
          </td>
          <td>
            {{ item.purpose }}
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">
            Responsable Maintenance
          </td>
          <td>
            <div>{{ item.mnt_responsible_technician }}</div>
            <div>{{ item.mnt_responsible_phone }}</div>
          </td>
          <td class="font-weight-bold">
            Binôme
          </td>
          <td>
            <div>{{ item.mnt_resp_buddy }}</div>
            <div>{{ item.mnt_resp_buddy_phone }}</div>
          </td>
        </tr>
        <tr>
          <td class="font-weight-bold">
            Pré-Allumage
          </td>
          <td>
            <custom-color-chip :value="item.valid_visit" />
          </td>
          <td class="font-weight-bold">
            Présence ECS
          </td>
          <td>
            <custom-color-chip :value="item.ECS_existence" />
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <v-container>
      <v-row justify="center">
        <v-col cols="12" class="text-center pa-0 mb-4">
          <h3>Automates</h3>
        </v-col>
      </v-row>
      <loader :is-loading="isLoadingController">
        <v-data-table
          dense
          :headers="cHeaders"
          :items="controllers"
          :items-per-page="100"
          height="97"
          hide-default-footer
          fixed-header
          :sort-by="['communication_status']"
          class="elevation-1"
          v-if="controllers && controllers.length"
        >
          <template v-slot:item.communication_status="{ item }">
            <custom-color-chip :value="item.communication_status" />
          </template>
          <template v-slot:item.date_last_communication="{ item }">
            <div v-if="item.date_last_communication">
              {{ new Date(item.date_last_communication).toLocaleDateString() }}
              {{ new Date(item.date_last_communication).toLocaleTimeString() }}
            </div>
          </template>
        </v-data-table>

        <div v-else class="caption font-italic">
          {{ generateMessage('heatingSetupParams.fields.controllers') }}
        </div>
      </loader>
    </v-container>

    <v-simple-table id="editable-table" class="secondarybackground">
      <v-icon v-if="isEditing" medium color="primary">edit</v-icon>
      <v-icon v-else medium color="primary">mdi-plus</v-icon>
      <tbody>
        <tr>
          <td class="font-italic">
            PEM/Bâtisseur
          </td>
          <td>
            <custom-color-chip editable v-model="param.PEM_action_validation" />
          </td>
          <td />
          <td />
        </tr>
        <tr>
          <td class="font-italic">
            Allumage à distance
          </td>
          <td>
            <custom-color-chip
              editable
              v-model="param.REE_activation_validation"
            />
          </td>
          <td class="font-italic">
            Arrêt à distance
          </td>
          <td>
            <custom-color-chip editable v-model="param.REE_stop_validation" />
          </td>
        </tr>
        <tr>
          <td class="font-italic">
            Mode *
          </td>
          <td>
            <v-radio-group v-model="param.activation_type">
              <v-radio
                v-for="item in ['PEM', 'Technicien', 'Autonome']"
                :key="item"
                :label="item"
                :value="item"
              ></v-radio>
            </v-radio-group>
          </td>
          <td class="font-italic">
            Commentaire
            {{ requiredComment ? ' *' : '' }}
          </td>
          <td>
            <v-textarea
              dense
              outlined
              hide-details
              v-model="param.agency_entity_comment"
              :placeholder="
                requiredComment
                  ? 'Un commentaire doit être renseigné en cas de refus (10 caractères minimum).'
                  : ''
              "
            />
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <template v-slot:actions>
      <div class="d-flex">
        <v-btn
          v-if="isEditing"
          small
          color="warning"
          :loading="isLoading"
          @click="confirmDeleteDialog = true"
          class="mx-2 text-validate-btn"
        >
          {{ generateMessage('requests.actions.delete_param') }}
        </v-btn>
        <v-btn
          v-else
          small
          color="primary"
          :loading="isLoading"
          disabled
          class="mx-2 text-validate-btn"
        >
          {{ generateMessage('requests.actions.delete_param') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="isEditing"
          small
          color="primary"
          :loading="isLoading"
          :disabled="
            requiredComment &&
              !(
                param.agency_entity_comment &&
                param.agency_entity_comment.length > 10
              )
          "
          @click="update"
          class="mx-2 text-validate-btn"
        >
          {{ generateMessage('requests.actions.validate') }}
        </v-btn>
        <v-btn
          v-else
          small
          color="primary"
          :loading="isLoading"
          :disabled="
            !validTNC ||
              !param.activation_type ||
              (requiredComment &&
                !(
                  param.agency_entity_comment &&
                  param.agency_entity_comment.length > 10
                ))
          "
          @click="create"
          class="mx-2 text-validate-btn"
        >
          {{ generateMessage('requests.actions.validate') }}
        </v-btn>
        <v-btn small class="mx-2" @click="cancel">
          {{ generateMessage('requests.actions.cancel') }}
        </v-btn>
      </div>
      <v-dialog v-model="confirmDeleteDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">Confirmer la suppression</v-card-title>
          <v-card-text>
            Êtes-vous sûr de vouloir supprimer ce paramétrage ?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="confirmDeleteDialog = false">
              Annuler
            </v-btn>
            <v-btn color="warning" text @click="handleDelete">Supprimer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </custom-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { generateMessage } from '@/utils/i18n';
import CustomDialog from '@/components/dialogs/CustomDialog';
import rejectRequest from '@/services/api/internal/requestActions/rejection.api';
import { setDispActive } from '@/services/api/internal/requestActions/selfAssign.api';
import CustomColorChip from '@/components/selects/CustomColorChip';
import SetupParamsService from '@/services/services/heatingignition/setup-params.service';
import { catchCRUDError } from '@/business/apiTreatment/response/errorTreatment';
import { TRANSMITTER_TYPES } from '@/store/modules/properties/constants';
import { getPredityPropertyControllersData } from '@/services/services/heatingignition/assets_predity_properties.service';
import Loader from '@/components/widgets/Loader';

function createParams(item, isEditing) {
  const res = {};
  if (isEditing) {
    res.REE_activation_validation = item.REE_activation_validation;
    res.REE_stop_validation = item.REE_stop_validation;
    res.PEM_action_validation = item.PEM_action_validation;
    res.ECS_existence = item.ECS_existence;
    res.activation_type = item.activation_type;
    res.agency_entity_comment = item.agency_entity_comment;
    res.id = item.id;
  } else {
    res.REE_activation_validation = null;
    res.REE_stop_validation = null;
    res.PEM_action_validation = null;
    res.ECS_existence = null;
    res.activation_type = null;
    res.agency_entity_comment = null;
    res.transmitter_type = null;
    res.communicating_transmitter = null;
    res.TNC_value = null;
    res.property_id = item.id;
  }

  return res;
}

export default {
  name: 'SetupParamsDialog',
  components: {
    CustomDialog,
    CustomColorChip,
    Loader,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: false,
    },
  },
  methods: {
    generateMessage,
    rejectRequest,
    setDispActive,
    cancel() {
      this.$refs.dialog.handleCloseClick();
    },
    update() {
      this.isLoading = true;
      SetupParamsService.updateSetupParams(this.param)
        .then(() => {
          this.cancel();
          this.$store.dispatch('properties/refreshItems');
          this.$store.dispatch('properties/ignitions/refreshItems');
        })
        .catch((err) => {
          this.$store.dispatch(
            'snackbar/setSnackbarOpen',
            catchCRUDError('EDIT', err)
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleDelete() {
      this.confirmDeleteDialog = false;
      this.isLoading = true;
      SetupParamsService.deleteItem(this.param.id)
        .then(() => {
          this.cancel();
          this.$store.dispatch('properties/refreshItems');
          this.$store.dispatch('properties/ignitions/refreshItems');
        })
        .catch((err) => {
          this.$store.dispatch(
            'snackbar/setSnackbarOpen',
            catchCRUDError('DELETE', err)
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    checkTNC() {
      this.param.TNC_value = Math.trunc(this.param.TNC_value);
    },
    create() {
      this.isLoading = true;

      SetupParamsService.createItem(this.param)
        .then(() => {
          this.cancel();
          this.$store.dispatch('properties/refreshItems');
          this.$store.dispatch('properties/ignitions/refreshItems');
        })
        .catch((err) => {
          this.$store.dispatch(
            'snackbar/setSnackbarOpen',
            catchCRUDError('ADD', err)
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadControllers(val) {
      this.controllers = null;
      this.isLoadingController = true;
      getPredityPropertyControllersData(val)
        .then((data) => {
          this.controllers = data;
        })
        .finally(() => {
          this.isLoadingController = false;
        });
    },
  },
  watch: {
    item(newItem) {
      if (newItem.id !== this.param.id) {
        this.param = createParams(newItem, this.isEditing);
        this.loadControllers(newItem.id);
      }
    },
  },
  mounted() {
    this.loadControllers(this.item.id);
  },
  computed: {
    ...mapGetters({ darkThemeEnabled: 'boilerplate/darkThemeEnabled' }),
    validTNC() {
      return this.param.TNC_value >= 0 && this.param.TNC_value <= 30;
    },
    requiredComment() {
      return (
        this.param.REE_activation_validation === false ||
        this.param.REE_stop_validation === false ||
        this.param.PEM_action_validation === false
      );
    },
  },
  data() {
    return {
      param: createParams(this.item, this.isEditing),
      isLoading: false,
      TRANSMITTER_TYPES: TRANSMITTER_TYPES.map((obj) => obj.name),
      isLoadingController: false,
      controllers: null,
      cHeaders: [
        {
          text: 'Nom',
          align: 'start',
          sortable: false,
          value: 'name',
        },
        {
          text: 'Code',
          align: 'start',
          sortable: false,
          value: 'code',
        },
        {
          text: 'Type',
          align: 'start',
          sortable: false,
          value: 'type_name',
        },
        { text: 'Etat Comm.', value: 'communication_status' },
        { text: 'Der. Comm.', value: 'date_last_communication' },
      ],
      confirmDeleteDialog: false,
    };
  },
};
</script>

<style scoped>
#editable-table {
  border: solid 2px #008cd0;
  margin-top: 15px;
  padding-bottom: 10px;
}

.non-editable-table-dark tbody {
  background-color: #424242 !important;
}
.non-editable-table-light tbody {
  background-color: #f1f1f1 !important;
}
#non-editable-table tbody tr:hover {
  background-color: transparent !important;
}
</style>
